import React, { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import * as utilParam from '@utiljs/param';
import request, { IServerResponse, RequestError } from '@/utils/request';

/**
 * 针对 `@/utils/request` 的 get 请求用 `useSWR` 封装的 hook
 * @param url 请求路径，可以不用手工构造 queryString，而是通过 `buildParams` 构造一个非嵌套的对象
 * @param buildParams 构造请求参数的函数，只支持非嵌套的键值对。`useGetRequest` 会构造相应的 queryString
 * @param config 其他请求配置，如 `{ timeout: 2000, headers: { 'X-Requested-With': 'XMLHttpRequest' } };`
 * @param toastOnError 默认为 `true`。此参数为 `true` 时，会在请求错误或业务 `code` 不为0时弹出相应的toast
 * @returns
 * - `data` `error` `isValidating` `mutate` 参见 [`useSWR` API](https://swr.vercel.app/docs/options)
 * - `loading` 是否处于加载状态
 */
export function useGetRequest<Q extends Record<string, string | number> = any, S = any>(
    url: string,
    buildParams?: () => Q,
    config?: any,
    toastOnError = true,
) {
    const getFetcher = useCallback(
        (url: string) => request.get<S>(url, undefined, config, toastOnError),
        [config, toastOnError],
    );

    // 注意 `@/utils/request` 的 `get` 本身是支持使用对象传参的
    // 但这里我们希望调用 `useGetRequest` 时两种传参方式的参数相同时，swr 拥有相同的 key
    // 因此这里手工构造 url 作为 key
    const key = buildParams ? `${url}?${utilParam.stringify(buildParams())}` : url;

    const { data, error, isValidating, mutate } = useSWR<IServerResponse<S>, RequestError<S>>(
        key,
        getFetcher,
    );

    return {
        data: data?.data,
        error,
        isValidating,
        mutate,
        loading: !error && !data,
    };
}

/**
 * 针对 `@/utils/request` 的 post 请求用 `useSWR` 封装的 hook
 * @param url 请求路径
 * @param buildParams 构造请求参数的函数，该函数会在 `useEffect` 中调用。
 * @param deps 调用 `buildParams` 的 `useEffect` 的依赖数组
 * @param config 其他请求配置，如 `{ timeout: 2000, headers: { 'X-Requested-With': 'XMLHttpRequest' } };`
 * @param toastOnError 默认为 `true`。此参数为 `true` 时，会在请求错误或业务 `code` 不为0时弹出相应的toast
 * @returns
 * - `data` `error` `isValidating` `mutate` 参见 [`useSWR` API](https://swr.vercel.app/docs/options)
 * - `loading` 是否处于加载状态
 */
export function usePostRequest<Q = any, S = any>(
    url: string,
    buildParams?: () => Q,
    deps?: React.DependencyList,
    config?: any,
    toastOnError = true,
) {
    const postFetcher = useCallback(
        (url: string, params: Q) => request.post<S>(url, params, config, toastOnError),
        [config, toastOnError],
    );

    // 参见 https://swr.vercel.app/docs/arguments#passing-objects
    // 不要直接给 `useSWR` 传 `buildParams()`（每次都会生成一个新对象）
    // 这样会导致 `useSWR` 认为 `key` 更新了，触发请求，进而触发渲染，然后又构造了新对象...持续以上过程导致无限请求与渲染
    const [params, setParams] = useState<Q>();

    // 注意这里返回的是 `IServerResponse<S>`，而不是 `S`
    // 因为 `loading` 状态的判断依赖于返回数据，falsy 的 `S` 可能影响判断，而 `IServerResponse<S>` 一定不是 falsy 的
    const { data, error, isValidating, mutate } = useSWR<IServerResponse<S>, RequestError<S>>(
        // 开始时 `params` 为 `undefined`
        // 如果未传 `buildParams` 参数，说明这个 post 请求的 body 为空，这种情况下应当发起请求（即 key 为 `[url]`）
        // 否则无需发起请求（即 key 为 `null`）
        params ? [url, params] : buildParams ? null : [url],
        postFetcher,
    );

    // 当 `deps` 更新时调用 `buildParams` 从而实现 `deps` 更新时触发新的请求
    useEffect(() => {
        setParams(buildParams?.());
    }, deps);

    return {
        data: data?.data,
        error,
        isValidating,
        mutate,
        loading: !error && !data,
    };
}
